import React from 'react'
import PhotoCropper from '../../../../../pages/public/modals/photoEdit/photoCropper'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup'
import { UserPhoto } from '../../../../../../../common/types/user'
import blankImage from '../../../../../assets/images/no_profile_image@2x.png'
import { MenuItem, MenuItemCommandEvent } from 'primereact/menuitem'
import { Image } from 'primereact/image'
import { swapPhotos,deletePhoto,confirmPhotoUpload,requestMediaUrl,replacePhoto } from '_services/users.service'
import { PopupContext, PopUpContextType} from '../../../../../_core/contexts/popToast'
import { uploadImageToPresignedUrl } from '_services/fetches/S3upload'
import { useFetchV2 } from '../../../../../_core/hooks'
import { _getV1 } from '_services/config'
import Skeleton from '../../../../../components/skeleton'
import MenuButton from '../../../../../components/menu-button'

interface UploadProps {
  onLoad?:(_:UserPhoto[]) => Promise<void> | void
  onChange?:(_:UserPhoto[]) => Promise<void> | void
}

const PhotoUpload : React.FC<UploadProps> = (props) => {
  const [images,reload] = useFetchV2<UserPhoto[],undefined>(async () => {
    const images = (await _getV1('/images')).data
    props.onLoad && props.onLoad(images)
    return images
  },undefined,[])
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)
  const [uploading,setUploading] = React.useState<null | true | number>(null)
  const [accepting,setAccepting] = React.useState<boolean>(false)
  // const [confirmDelete,setConfirmDelete] = React.useState<boolean>(false)
  const toast = React.useRef<Toast>(null)

  const primaryImageSelected = async (i:number) => {
    await swapPhotos(1,i)
    await (props.onChange && props.onChange(await reload(undefined)))
    openPopup({
      'severity':'success',
      'title':'Selection Confirmed',
      'text':'Profile photo selected',
    })
  }

  const deletePicture = async (images:UserPhoto[],i:number) => {
    if(images.length > 1)
    {
      await deletePhoto(i)
      await (props.onChange && props.onChange(await reload(undefined)))
      openPopup({
        'severity':'success',
        'title':'Photo Deletion',
        'text':'Photo Deleted successfully',
      })
    }
    else
    {
      openPopup({
        'severity':'warn',
        'title':'Photo Deletion',
        'text':'Can not delete the last photo; please select another photo to be primary before deleting.',
        'duration':5000,
      })
    }
  }

  const doUpload = async (blob:Blob) => {
    if(!!uploading)
    {
      const { id,url } = await requestMediaUrl(blob.type)
      await uploadImageToPresignedUrl(url,blob)
      if(uploading === true)
      {
        await confirmPhotoUpload(id)
      }
      else
      {
        await replacePhoto(uploading,id)
      }
      await (props.onChange && props.onChange(await reload(undefined)))
      setUploading(null)
      openPopup({ 'severity': 'success', 'title': 'Photo Uploaded', 'text': 'Upload photo successful', 'duration': 3000 })
    }
  }

  const items = (images:UserPhoto[],x:{ imgUrl:string,sort:number }) : MenuItem[] => [
    {
      'label': 'Options',
      'items': [
        {
          'label': 'Replace',
          'icon': 'pi pi-sync',
          'command': (e:MenuItemCommandEvent) => x.sort === 1 ? showTemplate(e.originalEvent.currentTarget as HTMLElement,x.sort) : setUploading(x.sort),
        },
        {
          'label': 'Delete',
          'icon': 'pi pi-trash',
          'command': (_:MenuItemCommandEvent) => deletePicture(images,x.sort),
        },
      ].concat(x.sort !== 1 ? [{
        'label': 'Make Primary',
        'icon': 'pi pi-star',
        'command': (_:MenuItemCommandEvent) => primaryImageSelected(x.sort)
      }] : [])
    }
  ]

  const showTemplate = (e:HTMLElement ,i:number | true | null) => {
    setAccepting(true)
    setUploading(i)
    confirmPopup({
      'target': e,
      'message': (
        <div className="flex justify-content-center align-items-center w-full gap-3 border-bottom-1 surface-border">
          <i className="pi pi-exclamation-circle d-flex justify-content-center text-center rounded py-1" style={{ color: 'orange', fontSize: '50px', background: 'floralwhite' }}></i>
          <p><b>This image will be your profile's initial photo!</b></p>
          <p >Please follow these step's for good results :</p>
          <ul className='px-4'>
            <li>Only you and no one else.</li>
            <li>Proper lighting.</li>
            <li>Un-obstructed face.</li>
          </ul>
          <p>Can be replaced after it has been added!!</p>
          <span>Please confirm to proceed moving forward.</span>
        </div>
      ),
      'acceptIcon': 'pi pi-check',
      'rejectIcon': 'pi pi-times',
      'accept':() => {
        setAccepting(false)
      },
      'reject':() => {
        setAccepting(false)
        setUploading(null)
        openPopup({ 'severity': 'warn', 'title': 'Cancelled', 'text': 'Upload cancelled', 'duration': 3000 })
      },
    })
  }

  const previewIcon = (<i className="fs-3 pi pi-search"></i>)

  return <>
      <p className="fs-6 text-center">Click the <span style={{ color: '#2296f3', fontWeight: 'bold' }}>Blue</span> icons to Add your photos, then the orange link when done.</p>
      <PhotoCropper isVisible={!!uploading && !accepting} onClose={() => setUploading(null)} doUpload={doUpload} />
      <ConfirmPopup />
      <Toast ref={toast}></Toast>
      <Skeleton
        dependency={images}
        structure={{
          'style':{ 'display': 'grid', 'gridTemplateColumns': 'repeat(2, 1fr)', 'rowGap': '.5rem', 'columnGap': '.5rem' },
          'children':new Array(6).fill({ 'style':{ 'padding':'50% 0','width':'200px' } }),
        }}
      >{(images) =>
      <div
        className="w-100"
        style={{ 'display': 'grid', 'gridTemplateColumns': 'repeat(2, 1fr)', 'rowGap': '.5rem', 'columnGap': '.5rem' }}
      >
        {images
          .toSorted((a,b) => a.sort - b.sort)
          .concat(Array(Math.max(6,images.length) - images.length).fill(null))
          .map((x:UserPhoto | null,i) => (
            <div key={i} className="rounded position-relative m-auto">
              <Image
                className={[
                  'rounded-4 overflow-hidden',
                  i === 0 ? 'first-photo' : undefined,
                ].join(' ')}
                src={x ? x.imgUrl : blankImage}
                alt="user image"
                width='200'
                preview
                indicatorIcon={previewIcon}
              />
              <span className="position-absolute bottom-0 end-0 p-1">
                {x
                  ?
                  <MenuButton
                    className="rounded-circle p-1"
                    items={items(images,x)}
                    menuProps={{
                      'popup':true,
                      'popupAlignment':'left',
                    }}
                  ></MenuButton>
                  :
                  <Button
                    type="button"
                    className="rounded-circle p-1"
                    icon="pi pi-plus"
                    onClick={(e) => i == 0 ? showTemplate(e.currentTarget,true) : setUploading(true)}
                  />
                }
              </span>
            </div>
          ))
        }
      </div>
      }</Skeleton>
  </>
}
export default PhotoUpload
